import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { axiosInstance } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Check, RefreshCw, Trash2, AlertCircle, AlertTriangle, Info, ShieldCheck, Edit } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import BlockingCommitmentsModal from '@/components/commitments/BlockingCommitmentsModal';
import EditPulseEntryDialog from '@/components/commitments/EditPulseEntryDialog';
import EditHistoryDialog from '@/components/commitments/EditHistoryDialog';
import AttendanceGate from '@/components/attendance/AttendanceGate';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import BulkPulseEntryGrid from '@/components/pulse/BulkPulseEntryGrid';

export default function PulseEntry() {
  const [gyms, setGyms] = useState([]);
  const [sources, setSources] = useState([]);
  const [pulses, setPulses] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unverifiedCount, setUnverifiedCount] = useState(0);
  const [blockingCommitments, setBlockingCommitments] = useState([]);
  const [showBlockingModal, setShowBlockingModal] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  // Initialize selectedDate with a function to ensure it gets today's date in USER's local timezone
  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [showVerificationConfirm, setShowVerificationConfirm] = useState(false);
  const [showFDZeroConfirm, setShowFDZeroConfirm] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editHistoryDialogOpen, setEditHistoryDialogOpen] = useState(false);
  const [selectedEntryForEdit, setSelectedEntryForEdit] = useState(null);
  const [entryMode, setEntryMode] = useState('bulk');

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  const [formData, setFormData] = useState({
    gym_id: '',
    pulse_id: '',
    source_id: '',
    dialed: '',
    dialed_company_ivr: '',
    dialed_fd_phone: '',
    connected: '',
    visit_scheduled: '',
    visit_done: '',
    closure_expected: '',
    revenue_expected: '',
    closure_phone: '',
    closure_counter: '',
    fd_availability: 0,
    comments: '',
    tl_verified: false,
  });

  // Auto-populated fields
  const [autoFields, setAutoFields] = useState({
    gym_name: '',
    tl_id: '',
    tl_name: '',
    manager_id: '',
    manager_name: '',
  });

  // Selected source info for validation
  const selectedSource = useMemo(() => {
    return sources.find(s => s.source_id === formData.source_id);
  }, [sources, formData.source_id]);

  // Calculate max date (today in user's local timezone) - updates when selectedDate changes (including at midnight)
  const maxDate = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, [selectedDate]);

  useEffect(() => {
    fetchMasterData();
    fetchUnverifiedCount();
    fetchBlockingCommitments();
  }, []);

  // Update selectedDate at midnight (in user's local timezone) to ensure max date is always current
  useEffect(() => {
    const updateDateAtMidnight = () => {
      const now = new Date();
      const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
      const msUntilMidnight = tomorrow - now;

      const timeoutId = setTimeout(() => {
        // Use local date, not UTC
        const localNow = new Date();
        const year = localNow.getFullYear();
        const month = String(localNow.getMonth() + 1).padStart(2, '0');
        const day = String(localNow.getDate()).padStart(2, '0');
        setSelectedDate(`${year}-${month}-${day}`);
        // Set up next day's timer
        updateDateAtMidnight();
      }, msUntilMidnight);

      return () => clearTimeout(timeoutId);
    };

    const cleanup = updateDateAtMidnight();
    return cleanup;
  }, []);

  useEffect(() => {
    fetchEntries();
    fetchUnverifiedCount();
    fetchBlockingCommitments();
  }, [selectedDate]);

  // Helper to check if value is a valid non-negative integer
  const isValidInteger = (value) => {
    if (value === '' || value === null || value === undefined) return true;
    const num = Number(value);
    return Number.isInteger(num) && num >= 0;
  };

  // Real-time validation
  useEffect(() => {
    const errors = {};
    const dialed = parseInt(formData.dialed) || 0;
    const connected = parseInt(formData.connected) || 0;
    const scheduled = parseInt(formData.visit_scheduled) || 0;
    const done = parseInt(formData.visit_done) || 0;

    // Negative number validation (VAL-004)
    if (formData.dialed && Number(formData.dialed) < 0) {
      errors.dialed = 'Value cannot be negative';
    }
    if (formData.connected && Number(formData.connected) < 0) {
      errors.connected = 'Value cannot be negative';
    }
    if (formData.visit_scheduled && Number(formData.visit_scheduled) < 0) {
      errors.visit_scheduled = 'Value cannot be negative';
    }
    if (formData.visit_done && Number(formData.visit_done) < 0) {
      errors.visit_done = 'Value cannot be negative';
    }
    if (formData.closure_expected && Number(formData.closure_expected) < 0) {
      errors.closure_expected = 'Value cannot be negative';
    }
    if (formData.closure_expected && Number(formData.closure_expected) > 50) {
      errors.closure_expected = 'Maximum 50 closures allowed per pulse';
    }
    if (formData.closure_phone && Number(formData.closure_phone) < 0) {
      errors.closure_phone = 'Value cannot be negative';
    }
    if (formData.closure_counter && Number(formData.closure_counter) < 0) {
      errors.closure_counter = 'Value cannot be negative';
    }
    if (formData.fd_availability !== undefined && Number(formData.fd_availability) < 0) {
      errors.fd_availability = 'Value cannot be negative';
    }

    // Decimal validation - integers only (VAL-005)
    if (formData.dialed && !isValidInteger(formData.dialed)) {
      errors.dialed = 'Must be a whole number (no decimals)';
    }
    if (formData.connected && !isValidInteger(formData.connected)) {
      errors.connected = 'Must be a whole number (no decimals)';
    }
    if (formData.visit_scheduled && !isValidInteger(formData.visit_scheduled)) {
      errors.visit_scheduled = 'Must be a whole number (no decimals)';
    }
    if (formData.visit_done && !isValidInteger(formData.visit_done)) {
      errors.visit_done = 'Must be a whole number (no decimals)';
    }
    if (formData.closure_expected && !isValidInteger(formData.closure_expected)) {
      errors.closure_expected = 'Must be a whole number (no decimals)';
    }
    if (formData.closure_phone && !isValidInteger(formData.closure_phone)) {
      errors.closure_phone = 'Must be a whole number (no decimals)';
    }
    if (formData.closure_counter && !isValidInteger(formData.closure_counter)) {
      errors.closure_counter = 'Must be a whole number (no decimals)';
    }
    if (formData.fd_availability !== undefined && !isValidInteger(formData.fd_availability)) {
      errors.fd_availability = 'Must be a whole number (no decimals)';
    }

    // Connected ≤ Dialed validation (only if both are valid)
    if (formData.connected && !errors.connected && !errors.dialed && connected > dialed) {
      errors.connected = 'Connected cannot exceed Dialed';
    }

    // Dialed calls source validation: Company IVR + FD Phone must equal Dialed
    const dialedCompanyIvr = parseInt(formData.dialed_company_ivr) || 0;
    const dialedFdPhone = parseInt(formData.dialed_fd_phone) || 0;
    const totalSourceCalls = dialedCompanyIvr + dialedFdPhone;

    if (formData.dialed && !errors.dialed && totalSourceCalls !== dialed) {
      errors.dialed_company_ivr = `Company IVR + FD Phone must equal ${dialed} (currently ${totalSourceCalls})`;
      errors.dialed_fd_phone = `Company IVR + FD Phone must equal ${dialed} (currently ${totalSourceCalls})`;
    }

    // Validate individual source fields are integers
    if (formData.dialed_company_ivr && !isValidInteger(formData.dialed_company_ivr)) {
      errors.dialed_company_ivr = 'Must be a whole number (no decimals)';
    }
    if (formData.dialed_fd_phone && !isValidInteger(formData.dialed_fd_phone)) {
      errors.dialed_fd_phone = 'Must be a whole number (no decimals)';
    }

    // Validate non-negative
    if (formData.dialed_company_ivr && Number(formData.dialed_company_ivr) < 0) {
      errors.dialed_company_ivr = 'Value cannot be negative';
    }
    if (formData.dialed_fd_phone && Number(formData.dialed_fd_phone) < 0) {
      errors.dialed_fd_phone = 'Value cannot be negative';
    }

    // Visit Done ≤ Scheduled validation (only if both are valid)
    if (formData.visit_done && !errors.visit_done && !errors.visit_scheduled && done > scheduled) {
      errors.visit_done = 'Visit Done cannot exceed Scheduled';
    }

    // Visit Scheduled required if source needs visit
    if (selectedSource?.needs_visit === 'Yes' && !formData.visit_scheduled && formData.source_id) {
      errors.visit_scheduled = 'Required for this source type';
    }

    // Revenue validation (Phase 12)
    const closureExpected = parseInt(formData.closure_expected) || 0;
    const revenueExpected = parseFloat(formData.revenue_expected) || 0;

    // Rule 1: Revenue mandatory when closure expected > 0
    if (closureExpected > 0 && revenueExpected <= 0) {
      errors.revenue_expected = 'Revenue is required when closure expected > 0';
    }

    // Rule 2: No revenue when no closures expected
    if (closureExpected === 0 && revenueExpected > 0) {
      errors.revenue_expected = 'Cannot have revenue without closure expected';
    }

    setValidationErrors(errors);
  }, [formData, selectedSource]);

  // Helper to parse time string "HH:MM" to decimal hours
  const parseTimeToHours = (timeStr) => {
    if (!timeStr) return 0;
    const parts = timeStr.split(':');
    return parseInt(parts[0], 10) + (parts.length > 1 ? parseInt(parts[1], 10) / 60 : 0);
  };

  // Check if pulse entry is allowed based on Master Config times
  // Entry allowed: from pulse start until entry_deadline (from DB config)
  const isPulseActive = (pulseId) => {
    if (!pulseId || !pulses.length) return false;

    const pulseConfig = pulses.find(p => p.pulse_id === pulseId);
    if (!pulseConfig) return false;

    const now = new Date();
    const utcHours = now.getUTCHours();
    const utcMinutes = now.getUTCMinutes();
    // Convert UTC to IST (UTC + 5:30)
    const istHour = utcHours + 5 + (utcMinutes + 30) / 60;

    const startHour = parseTimeToHours(pulseConfig.time_start);
    const endHour = parseTimeToHours(pulseConfig.time_end);
    // Use entry_deadline from config, default to end + 30min if not set
    const deadlineHour = pulseConfig.entry_deadline
      ? parseTimeToHours(pulseConfig.entry_deadline)
      : endHour + 0.5;

    // Allow from pulse start until entry deadline
    return istHour >= startHour && istHour < deadlineHour;
  };

  // Check if pulse has started (is selectable) - pulse can only be selected after its start time
  const isPulseSelectable = (pulseId) => {
    if (!pulseId || !pulses.length) return false;

    const pulseConfig = pulses.find(p => p.pulse_id === pulseId);
    if (!pulseConfig) return false;

    const now = new Date();
    const utcHours = now.getUTCHours();
    const utcMinutes = now.getUTCMinutes();
    // Convert UTC to IST (UTC + 5:30)
    const istHour = utcHours + 5 + (utcMinutes + 30) / 60;

    const startHour = parseTimeToHours(pulseConfig.time_start);

    // Pulse is selectable only after its start time
    return istHour >= startHour;
  };

  // Format pulse display text with time window from Master Config
  const formatPulseDisplay = (pulse) => {
    const deadline = pulse.entry_deadline ||
      `${String(Math.floor(parseTimeToHours(pulse.time_end) + 0.5)).padStart(2, '0')}:${String(Math.round(((parseTimeToHours(pulse.time_end) + 0.5) % 1) * 60)).padStart(2, '0')}`;
    const isSelectable = isPulseSelectable(pulse.pulse_id);
    if (!isSelectable) {
      return `${pulse.name} (starts at ${pulse.time_start} IST)`;
    }
    return `${pulse.name} (${pulse.time_start}-${pulse.time_end}, submit by ${deadline})`;
  };

  const fetchMasterData = async () => {
    try {
      const [gymsRes, pulsesRes, sourcesRes] = await Promise.all([
        axiosInstance.get('/gyms'),
        axiosInstance.get('/pulses'),
        axiosInstance.get('/sources'),
      ]);
      
      let userGyms = gymsRes.data.filter(g => g.status === 'Active');
      if (user.role === 'team_leader') {
        userGyms = gymsRes.data.filter(g => g.tl_id === user.linked_id && g.status === 'Active');
      }
      
      setGyms(userGyms);
      setPulses(pulsesRes.data);
      setSources(sourcesRes.data);
    } catch (error) {
      toast.error('Failed to load master data');
    }
  };

  const fetchEntries = async () => {
    try {
      const params = { date: selectedDate };
      if (user.role === 'team_leader') {
        params.tl_id = user.linked_id;
      }
      const response = await axiosInstance.get('/pulse-entries', { params });
      setEntries(response.data);
    } catch (error) {
      toast.error('Failed to load entries');
    }
  };

  const fetchUnverifiedCount = async () => {
    try {
      const response = await axiosInstance.get('/pulse-entries/unverified-count');
      setUnverifiedCount(response.data.unverified_count || 0);
    } catch (error) {
      console.error('Failed to load unverified count:', error);
      setUnverifiedCount(0);
    }
  };

  const fetchBlockingCommitments = async () => {
    try {
      const response = await axiosInstance.get('/pulse-entries/blocking-commitments');
      setBlockingCommitments(response.data.blocking_commitments || []);
    } catch (error) {
      console.error('Failed to load blocking commitments:', error);
      setBlockingCommitments([]);
    }
  };

  const fetchSuggestions = useCallback(async () => {
    // Clear suggestions if gym or pulse not selected
    if (!formData.gym_id || !formData.pulse_id) {
      setSuggestions(null);
      return;
    }

    setSuggestionsLoading(true);
    try {
      const res = await axiosInstance.get('/pulse-suggestions', {
        params: {
          gym_id: formData.gym_id,
          pulse_id: formData.pulse_id,
          date: selectedDate,
        },
      });
      setSuggestions(res.data);
    } catch (err) {
      console.error('Failed to fetch pulse suggestions:', err);
      setSuggestions(null);
    } finally {
      setSuggestionsLoading(false);
    }
  }, [formData.gym_id, formData.pulse_id, selectedDate]);

  // Fetch suggestions when gym, pulse, or date changes
  useEffect(() => {
    fetchSuggestions();
  }, [fetchSuggestions]);

  const handleGymChange = (gymId) => {
    const gym = gyms.find(g => g.gym_id === gymId);
    setFormData(prev => ({ ...prev, gym_id: gymId }));
    if (gym) {
      setAutoFields({
        gym_name: gym.name,
        tl_id: gym.tl_id,
        tl_name: gym.tl_name,
        manager_id: gym.manager_id,
        manager_name: gym.manager_name,
      });
    }
  };

  const handleDateChange = (newDate) => {
    const today = new Date().toISOString().split('T')[0];
    if (newDate > today) {
      toast.error('Cannot select future date');
      return;
    }
    setSelectedDate(newDate);
  };

  // Auto-calculate Connect %
  const connectPct = useMemo(() => {
    const dialed = parseInt(formData.dialed) || 0;
    const connected = parseInt(formData.connected) || 0;
    if (dialed > 0) return Math.round((connected / dialed) * 100);
    return 0;
  }, [formData.dialed, formData.connected]);

  // Auto-calculate Schedule Rate %
  const scheduleRate = useMemo(() => {
    const connected = parseInt(formData.connected) || 0;
    const scheduled = parseInt(formData.visit_scheduled) || 0;
    if (connected > 0) return Math.round((scheduled / connected) * 100);
    return 0;
  }, [formData.connected, formData.visit_scheduled]);

  // Auto-calculate Visit Done Rate %
  const visitDoneRate = useMemo(() => {
    const scheduled = parseInt(formData.visit_scheduled) || 0;
    const done = parseInt(formData.visit_done) || 0;
    if (scheduled > 0) return Math.round((done / scheduled) * 100);
    return 0;
  }, [formData.visit_scheduled, formData.visit_done]);

  // Auto-calculate Total Closure
  const totalClosure = useMemo(() => {
    return (parseInt(formData.closure_phone) || 0) + (parseInt(formData.closure_counter) || 0);
  }, [formData.closure_phone, formData.closure_counter]);

  // Auto-calculate Closure Rate %
  const closureRate = useMemo(() => {
    const expected = parseInt(formData.closure_expected) || 0;
    if (expected > 0) return Math.round((totalClosure / expected) * 100);
    return 0;
  }, [formData.closure_expected, totalClosure]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.gym_id || !formData.pulse_id || !formData.source_id) {
      toast.error('Please select Gym, Pulse, and Source');
      return;
    }

    // Check for validation errors
    if (Object.keys(validationErrors).length > 0) {
      toast.error('Please fix validation errors before submitting');
      return;
    }

    // Validate Connected ≤ Dialed
    const dialed = parseInt(formData.dialed) || 0;
    const connected = parseInt(formData.connected) || 0;
    if (connected > dialed) {
      toast.error('Connected cannot be greater than Dialed');
      return;
    }

    // Validate Visit Done ≤ Scheduled
    const scheduled = parseInt(formData.visit_scheduled) || 0;
    const done = parseInt(formData.visit_done) || 0;
    if (done > scheduled) {
      toast.error('Visit Done cannot be greater than Visit Scheduled');
      return;
    }

    // MANDATORY: TL must verify the entry before submission
    if (!formData.tl_verified) {
      toast.error('You must verify this entry before submitting. Please check the "TL Verified" checkbox.');
      return;
    }

    // Check if FD availability is zero - show confirmation before proceeding
    const fdValue = parseInt(formData.fd_availability);
    if (fdValue === 0 || formData.fd_availability === '0' || formData.fd_availability === 0) {
      setShowFDZeroConfirm(true);
      return;
    }

    // Show verification confirmation dialog
    setShowVerificationConfirm(true);
  };

  // Handle confirmed submission after verification popup
  const handleConfirmedSubmit = async () => {
    setShowVerificationConfirm(false);
    setLoading(true);

    const dialed = parseInt(formData.dialed) || 0;
    const connected = parseInt(formData.connected) || 0;
    const scheduled = parseInt(formData.visit_scheduled) || 0;
    const done = parseInt(formData.visit_done) || 0;

    try {
      await axiosInstance.post('/pulse-entries', {
        entry_date: selectedDate,
        gym_id: formData.gym_id,
        pulse_id: formData.pulse_id,
        source_id: formData.source_id,
        dialed: dialed,
        dialed_company_ivr: parseInt(formData.dialed_company_ivr) || 0,
        dialed_fd_phone: parseInt(formData.dialed_fd_phone) || 0,
        connected: connected,
        visit_scheduled: scheduled,
        visit_done: done,
        closure_expected: parseInt(formData.closure_expected) || 0,
        revenue_expected: parseFloat(formData.revenue_expected) || 0.0,
        closure_phone: parseInt(formData.closure_phone) || 0,
        closure_counter: parseInt(formData.closure_counter) || 0,
        fd_availability: parseInt(formData.fd_availability) || 0,
        comments: formData.comments,
        tl_verified: formData.tl_verified,
      });
      toast.success('Entry added successfully!');

      // Refresh unverified count after submission
      fetchUnverifiedCount();

      // Reset only numeric fields for quick re-entry
      setFormData(prev => ({
        ...prev,
        pulse_id: '',
        source_id: '',
        dialed: '',
        dialed_company_ivr: '',
        dialed_fd_phone: '',
        connected: '',
        visit_scheduled: '',
        visit_done: '',
        closure_expected: '',
        revenue_expected: '',
        closure_phone: '',
        closure_counter: '',
        fd_availability: 0,
        comments: '',
        tl_verified: false,
      }));
      fetchEntries();
    } catch (error) {
      const data = error.response?.data;
      if (data?.error === 'no_active_roster' || data?.error === 'not_checked_in') {
        // Use server's human-readable detail message
        toast.error(data?.detail || 'Please check in to your shift first. Go to My Roster to check in.');
      } else {
        toast.error(data?.detail || data?.error || 'Failed to add entry');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyTL = async (entryId) => {
    try {
      await axiosInstance.post(`/pulse-entries/${entryId}/verify-tl`);
      toast.success('TL Verified');
      fetchEntries();
    } catch (error) {
      toast.error('Failed to verify');
    }
  };

  const handleVerifyManager = async (entryId) => {
    try {
      await axiosInstance.post(`/pulse-entries/${entryId}/verify-manager`);
      toast.success('Manager Verified');
      fetchEntries();
    } catch (error) {
      toast.error('Failed to verify');
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await axiosInstance.delete(`/pulse-entries/${entryId}`);
      toast.success('Entry deleted');
      fetchEntries();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const getConnectColor = (pct) => {
    if (pct >= 70) return '#28A745';
    if (pct >= 60) return '#FFC107';
    return '#DC3545';
  };

  const getRateColor = (pct, target) => {
    if (pct >= target) return '#28A745';
    if (pct >= target - 10) return '#FFC107';
    return '#DC3545';
  };

  const openEditDialog = (entry) => {
    setSelectedEntryForEdit(entry);
    setEditDialogOpen(true);
  };

  const openEditHistory = (entry) => {
    setSelectedEntryForEdit(entry);
    setEditHistoryDialogOpen(true);
  };

  const handleConfirmAuto = async (entryId, overrideData) => {
    try {
      await axiosInstance.post(`/pulse-entries/${entryId}/confirm-auto`, overrideData || {});
      toast.success('Entry confirmed successfully');
      fetchEntries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to confirm entry');
    }
  };

  const handleEditSuccess = () => {
    fetchEntries();
  };

  // Format timestamp to IST timezone (DD-MM-YYYY HH:MM)
  const formatTimestampIST = (timestamp) => {
    if (!timestamp) return '-';
    try {
      // Ensure UTC designation
      const utcTimestamp = typeof timestamp === 'string' && !timestamp.endsWith('Z')
        ? timestamp + 'Z'
        : timestamp;

      const date = new Date(utcTimestamp);
      if (isNaN(date.getTime())) return '-';

      // Convert to IST using proper timezone
      const options = {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      };

      const parts = new Intl.DateTimeFormat('en-GB', options).formatToParts(date);
      const day = parts.find(p => p.type === 'day').value;
      const month = parts.find(p => p.type === 'month').value;
      const year = parts.find(p => p.type === 'year').value;
      const hours = parts.find(p => p.type === 'hour').value;
      const minutes = parts.find(p => p.type === 'minute').value;

      return `${day}-${month}-${year} ${hours}:${minutes}`;
    } catch (error) {
      return '-';
    }
  };

  // Format filled_by display (Name with role badge)
  const formatFilledBy = (entry) => {
    if (!entry.filled_by_name) return '-';

    // Map role to display format
    const roleLabels = {
      'admin': 'Admin',
      'manager': 'Manager',
      'team_leader': 'TL'
    };

    const roleLabel = roleLabels[entry.filled_by_role] || entry.filled_by_role;
    return `${entry.filled_by_name} (${roleLabel})`;
  };

  return (
    <div data-testid="pulse-entry-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold" style={{ color: '#1F4E79' }}>Pulse Entry</h1>
          <p className="text-sm text-gray-500">Enter daily activity data</p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={selectedDate}
            onChange={(e) => handleDateChange(e.target.value)}
            max={maxDate}
            className="w-36"
            data-testid="date-picker"
          />
          <Button variant="outline" size="sm" onClick={fetchEntries}>
            <RefreshCw size={16} />
          </Button>
        </div>
      </div>

      {/* Field Guide Reference */}
      <Card className="mb-4 bg-blue-50 border-blue-200">
        <CardContent className="p-3">
          <div className="text-xs text-blue-800">
            <strong>Validation Rules:</strong> Connected ≤ Dialed • Visit Done ≤ Scheduled • Visit Scheduled required if source needs visit
          </div>
        </CardContent>
      </Card>

      {/* Verification Required Banner */}
      {unverifiedCount > 0 && (
        <Card className="border-amber-500 bg-amber-50 mb-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-amber-900 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Verification Required
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-amber-800 text-sm">
              You have <strong>{unverifiedCount}</strong> unverified pulse {unverifiedCount === 1 ? 'entry' : 'entries'}.
              Please verify all entries in the <strong>Verification</strong> tab before submitting new pulses.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-3 border-amber-600 text-amber-900 hover:bg-amber-100"
              onClick={() => window.location.href = '/verification'}
            >
              Go to Verification
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Blocking Commitments Banner */}
      {blockingCommitments.length > 0 && (
        <Card className="border-amber-500 bg-amber-50 mb-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-amber-900 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Past Commitments Need Resolution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-amber-800 text-sm">
              You have <strong>{blockingCommitments.length}</strong> unfulfilled commitment{blockingCommitments.length === 1 ? '' : 's'} from past dates.
              Resolve them by moving to today/future or marking as fulfilled before submitting new pulses.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2 border-amber-500 text-amber-700 hover:bg-amber-100"
              onClick={() => setShowBlockingModal(true)}
            >
              View Commitments
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Entry Form - Gated by Attendance Check-in */}
      <AttendanceGate blockType="form" allowView={true}>
        <Card className="mb-6">
          <CardHeader className="pb-3 bg-[#1F4E79] text-white rounded-t-lg">
            <CardTitle className="text-base">New Entry</CardTitle>
          </CardHeader>
        <CardContent className="pt-4">
            {/* Shared selectors: Gym + Pulse (above tabs, used by both modes) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <div>
                <Label className="text-xs mb-1">Gym * <span className="text-gray-400">(Filter by TL's gyms)</span></Label>
                <Select value={formData.gym_id} onValueChange={handleGymChange}>
                  <SelectTrigger className="h-9" data-testid="gym-select">
                    <SelectValue placeholder="Select gym" />
                  </SelectTrigger>
                  <SelectContent>
                    {gyms.map(gym => (
                      <SelectItem key={gym.gym_id} value={gym.gym_id}>{gym.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs mb-1">Pulse * <span className="text-gray-400">(Only started pulses)</span></Label>
                <Select value={formData.pulse_id} onValueChange={(v) => setFormData(prev => ({ ...prev, pulse_id: v }))}>
                  <SelectTrigger className="h-9" data-testid="pulse-select">
                    <SelectValue placeholder="Select pulse" />
                  </SelectTrigger>
                  <SelectContent>
                    {pulses.map(pulse => {
                      const isSelectable = isPulseSelectable(pulse.pulse_id);
                      return (
                        <SelectItem
                          key={pulse.pulse_id}
                          value={pulse.pulse_id}
                          disabled={!isSelectable}
                          className={!isSelectable ? 'text-gray-400 opacity-60' : ''}
                        >
                          {formatPulseDisplay(pulse)}
                          {!isSelectable && ' 🔒'}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Auto-populated Info (Read-only) - shared */}
            {autoFields.gym_name && (
              <div className="bg-gray-100 border border-gray-300 rounded-lg p-3 mb-4 text-xs">
                <div className="text-gray-500 mb-1 font-semibold">Auto-Filled (Read-only):</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div><span className="text-gray-500">Gym:</span> <span className="font-medium">{autoFields.gym_name}</span></div>
                  <div><span className="text-gray-500">TL Name:</span> <span className="font-medium">{autoFields.tl_name}</span></div>
                  <div><span className="text-gray-500">Manager:</span> <span className="font-medium">{autoFields.manager_name}</span></div>
                  <div><span className="text-gray-500">TL ID:</span> <span className="font-medium">{autoFields.tl_id}</span></div>
                </div>
              </div>
            )}

            {/* Entry Mode Toggle */}
            <Tabs value={entryMode} onValueChange={setEntryMode} className="mb-4">
              <TabsList className="mb-4">
                <TabsTrigger value="bulk">Bulk Entry</TabsTrigger>
                <TabsTrigger value="single">Single Entry</TabsTrigger>
              </TabsList>

              {/* === SINGLE ENTRY TAB === */}
              <TabsContent value="single">
          <form onSubmit={handleSubmit}>
            {/* Source selector (single-entry only) */}
            <div className="mb-4">
              <Label className="text-xs mb-1">Source * <span className="text-gray-400">(All active sources)</span></Label>
              <Select value={formData.source_id} onValueChange={(v) => setFormData(prev => ({ ...prev, source_id: v }))}>
                <SelectTrigger className="h-9" data-testid="source-select">
                  <SelectValue placeholder="Select source" />
                </SelectTrigger>
                <SelectContent>
                  {sources.map(source => (
                    <SelectItem key={source.source_id} value={source.source_id}>
                      {source.name} {source.needs_visit === 'Yes' ? '(Visit)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Row 2: Dialed & Connected with Validation */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <div>
                <Label className="text-xs mb-1">Dialed * <span className="text-gray-400">(≥0, whole number)</span></Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  className={`h-9 ${validationErrors.dialed ? 'border-red-500' : ''}`}
                  value={formData.dialed}
                  onChange={(e) => setFormData(prev => ({ ...prev, dialed: e.target.value }))}
                  data-testid="dialed-input"
                />
                {validationErrors.dialed && (
                  <div className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <AlertCircle size={12} /> {validationErrors.dialed}
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs mb-1">Company IVR Calls * <span className="text-gray-400">(Compliance tracking)</span></Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  className={`h-9 ${validationErrors.dialed_company_ivr ? 'border-red-500' : ''}`}
                  value={formData.dialed_company_ivr}
                  onChange={(e) => setFormData(prev => ({ ...prev, dialed_company_ivr: e.target.value }))}
                  data-testid="dialed-company-ivr-input"
                  placeholder="0"
                />
                {validationErrors.dialed_company_ivr && (
                  <div className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <AlertCircle size={12} /> {validationErrors.dialed_company_ivr}
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs mb-1">FD Phone Calls * <span className="text-gray-400">(Compliance tracking)</span></Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  className={`h-9 ${validationErrors.dialed_fd_phone ? 'border-red-500' : ''}`}
                  value={formData.dialed_fd_phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, dialed_fd_phone: e.target.value }))}
                  data-testid="dialed-fd-phone-input"
                  placeholder="0"
                />
                {validationErrors.dialed_fd_phone && (
                  <div className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <AlertCircle size={12} /> {validationErrors.dialed_fd_phone}
                  </div>
                )}
                {formData.dialed_fd_phone && parseInt(formData.dialed_fd_phone) > 0 && (
                  <div className="text-amber-600 text-xs mt-1 flex items-center gap-1">
                    <AlertTriangle size={12} /> Please ensure this data is verified from the official IVR dashboard
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs mb-1">Connected * <span className="text-gray-400">(≤Dialed, whole number)</span></Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  className={`h-9 ${validationErrors.connected ? 'border-red-500' : ''}`}
                  value={formData.connected}
                  onChange={(e) => setFormData(prev => ({ ...prev, connected: e.target.value }))}
                  data-testid="connected-input"
                />
                {validationErrors.connected && (
                  <div className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <AlertCircle size={12} /> {validationErrors.connected}
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs mb-1">Connect % <span className="text-gray-400">(Auto)</span></Label>
                <div className="h-9 flex items-center px-3 bg-gray-100 border rounded-md font-semibold" style={{ color: getConnectColor(connectPct) }}>
                  {connectPct}%
                </div>
              </div>
              <div>
                <Label className="text-xs mb-1">
                  Visit Scheduled {selectedSource?.needs_visit === 'Yes' && <span className="text-red-500">*</span>}
                </Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  className={`h-9 ${validationErrors.visit_scheduled ? 'border-red-500' : ''}`}
                  value={formData.visit_scheduled}
                  onChange={(e) => setFormData(prev => ({ ...prev, visit_scheduled: e.target.value }))}
                  data-testid="visit-sched-input"
                />
                {validationErrors.visit_scheduled && (
                  <div className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <AlertCircle size={12} /> {validationErrors.visit_scheduled}
                  </div>
                )}
              </div>
            </div>

            {/* Row 3: Visit Done, Rates */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <div>
                <Label className="text-xs mb-1">Visit Done <span className="text-gray-400">(≤Scheduled, whole number)</span></Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  className={`h-9 ${validationErrors.visit_done ? 'border-red-500' : ''}`}
                  value={formData.visit_done}
                  onChange={(e) => setFormData(prev => ({ ...prev, visit_done: e.target.value }))}
                  data-testid="visit-done-input"
                />
                {validationErrors.visit_done && (
                  <div className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <AlertCircle size={12} /> {validationErrors.visit_done}
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs mb-1">Schedule Rate % <span className="text-gray-400">(Auto)</span></Label>
                <div className="h-9 flex items-center px-3 bg-gray-100 border rounded-md font-semibold" style={{ color: getRateColor(scheduleRate, 50) }}>
                  {scheduleRate}%
                </div>
              </div>
              <div>
                <Label className="text-xs mb-1">Visit Done Rate % <span className="text-gray-400">(Auto)</span></Label>
                <div className="h-9 flex items-center px-3 bg-gray-100 border rounded-md font-semibold" style={{ color: getRateColor(visitDoneRate, 50) }}>
                  {visitDoneRate}%
                </div>
              </div>
              <div>
                <Label className="text-xs mb-1 flex items-center gap-1">
                  Closure Expected *
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info size={12} className="text-gray-400 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">Number of membership closures you expect to complete this pulse (max 50). If you enter closures, you must also enter expected revenue.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  className={`h-9 ${validationErrors.closure_expected ? 'border-red-500' : ''}`}
                  value={formData.closure_expected}
                  onChange={(e) => setFormData(prev => ({ ...prev, closure_expected: e.target.value }))}
                  data-testid="closure-expected-input"
                />
                {validationErrors.closure_expected && (
                  <div className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <AlertCircle size={12} /> {validationErrors.closure_expected}
                  </div>
                )}
              </div>
            </div>

            {/* Revenue Expected - conditional display */}
            {parseInt(formData.closure_expected) > 0 && (
              <div className="mb-4">
                <Label className="text-xs mb-1 flex items-center gap-1">
                  Revenue Expected (₹) <span className="text-red-500">*</span>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info size={12} className="text-gray-400 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">Total revenue expected from all {formData.closure_expected} closures combined. Enter the sum of all membership packages you expect to sell.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Label>
                <Input
                  type="number"
                  step="1000"
                  min="0"
                  value={formData.revenue_expected}
                  onChange={(e) => setFormData(prev => ({ ...prev, revenue_expected: e.target.value }))}
                  className={`h-9 ${validationErrors.revenue_expected ? 'border-red-500' : ''}`}
                  placeholder="Enter total expected revenue"
                  data-testid="revenue-expected-input"
                />
                {validationErrors.revenue_expected && (
                  <div className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <AlertCircle size={12} /> {validationErrors.revenue_expected}
                  </div>
                )}
              </div>
            )}

            {/* Row 4: Closures */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <div>
                <Label className="text-xs mb-1">📞 Closure Phone <span className="text-gray-400">(≥0, whole number)</span></Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  className={`h-9 ${validationErrors.closure_phone ? 'border-red-500' : ''}`}
                  value={formData.closure_phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, closure_phone: e.target.value }))}
                  data-testid="closure-phone-input"
                />
                {validationErrors.closure_phone && (
                  <div className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <AlertCircle size={12} /> {validationErrors.closure_phone}
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs mb-1">🏪 Closure Counter <span className="text-gray-400">(≥0, whole number)</span></Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  className={`h-9 ${validationErrors.closure_counter ? 'border-red-500' : ''}`}
                  value={formData.closure_counter}
                  onChange={(e) => setFormData(prev => ({ ...prev, closure_counter: e.target.value }))}
                  data-testid="closure-counter-input"
                />
                {validationErrors.closure_counter && (
                  <div className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <AlertCircle size={12} /> {validationErrors.closure_counter}
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs mb-1">Total Closure <span className="text-gray-400">(Auto: Phone+Counter)</span></Label>
                <div className="h-9 flex items-center px-3 bg-green-100 border border-green-300 rounded-md font-bold text-green-700">
                  {totalClosure}
                </div>
              </div>
              <div>
                <Label className="text-xs mb-1">Closure Rate % <span className="text-gray-400">(Auto)</span></Label>
                <div className="h-9 flex items-center px-3 bg-gray-100 border rounded-md font-semibold" style={{ color: getRateColor(closureRate, 60) }}>
                  {closureRate}%
                </div>
              </div>
            </div>

            {/* FD Availability */}
            <div className="mb-4">
              <Label className="text-xs mb-1 flex items-center gap-1">
                FD Availability *
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info size={12} className="text-gray-400 cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">Number of front desk staff available during this pulse. This helps track gym capacity and operational efficiency.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </Label>
              <Input
                type="number"
                min="0"
                step="1"
                required
                className={`h-9 ${validationErrors.fd_availability ? 'border-red-500' : ''}`}
                value={formData.fd_availability}
                onChange={(e) => setFormData(prev => ({ ...prev, fd_availability: e.target.value }))}
                data-testid="fd-availability-input"
              />
              {validationErrors.fd_availability && (
                <div className="text-red-500 text-xs mt-1 flex items-center gap-1">
                  <AlertCircle size={12} /> {validationErrors.fd_availability}
                </div>
              )}
            </div>

            {/* Row 5: Comments and TL Verified */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <div>
                <Label className="text-xs mb-1">Comments <span className="text-gray-400">(Optional)</span></Label>
                <Textarea 
                  className="h-16 text-sm" 
                  placeholder="Any additional notes..."
                  value={formData.comments} 
                  onChange={(e) => setFormData(prev => ({ ...prev, comments: e.target.value }))} 
                  data-testid="comments-input" 
                />
              </div>
              <div className="flex flex-col justify-end">
                <div className={`flex items-center space-x-2 p-3 rounded-lg border-2 transition-colors ${
                  formData.tl_verified
                    ? 'bg-green-50 border-green-400'
                    : 'bg-red-50 border-red-300'
                }`}>
                  <Checkbox
                    id="tl-verified"
                    checked={formData.tl_verified}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, tl_verified: checked }))}
                    data-testid="tl-verified-checkbox"
                    className="h-5 w-5"
                  />
                  <Label htmlFor="tl-verified" className="text-sm font-medium cursor-pointer flex items-center gap-2">
                    {formData.tl_verified ? (
                      <>
                        <ShieldCheck size={16} className="text-green-600" />
                        <span className="text-green-700">Data Verified ✓</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle size={16} className="text-red-500" />
                        <span className="text-red-600">MANDATORY: Verify this data before submitting</span>
                      </>
                    )}
                  </Label>
                </div>
              </div>
            </div>

            {/* Pulse Timing Warning */}
            {!isPulseActive(formData.pulse_id) && formData.pulse_id && (
              <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="text-red-700 text-sm font-medium flex items-center gap-2">
                  <AlertCircle size={16} />
                  Submission window closed for {formData.pulse_id}
                </div>
                <div className="text-red-600 text-xs mt-1">
                  Allowed: {(() => {
                    const p = pulses.find(x => x.pulse_id === formData.pulse_id);
                    if (!p) return 'N/A';
                    const deadline = p.entry_deadline || `${p.time_end} + 30min`;
                    return `${p.time_start}-${p.time_end} IST (submit by ${deadline})`;
                  })()}
                </div>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              className={`w-full h-10 ${
                !formData.tl_verified || unverifiedCount > 0 || blockingCommitments.length > 0
                  ? 'bg-gray-400 hover:bg-gray-500'
                  : 'bg-[#1F4E79] hover:bg-[#163a5c]'
              }`}
              disabled={loading || Object.keys(validationErrors).length > 0 || !isPulseActive(formData.pulse_id) || unverifiedCount > 0 || blockingCommitments.length > 0 || !formData.tl_verified}
              onClick={(e) => {
                if (blockingCommitments.length > 0) {
                  e.preventDefault();
                  setShowBlockingModal(true);
                }
              }}
              data-testid="submit-entry-btn"
              data-track="pulse-submit-entry"
            >
              {loading ? 'Submitting...' :
               !isPulseActive(formData.pulse_id) && formData.pulse_id
                 ? `${formData.pulse_id} window closed`
                 : blockingCommitments.length > 0
                   ? `Resolve ${blockingCommitments.length} Commitment${blockingCommitments.length === 1 ? '' : 's'}`
                   : unverifiedCount > 0
                     ? `⚠️ ${unverifiedCount} Unverified ${unverifiedCount === 1 ? 'Entry' : 'Entries'}`
                     : !formData.tl_verified
                       ? '⚠️ Please verify data first'
                       : '✓ Submit Verified Entry'}
            </Button>
          </form>
              </TabsContent>

              {/* === BULK ENTRY TAB === */}
              <TabsContent value="bulk">
                <BulkPulseEntryGrid
                  sources={sources}
                  entries={entries}
                  selectedGymId={formData.gym_id}
                  selectedPulseId={formData.pulse_id}
                  selectedDate={selectedDate}
                  autoFields={autoFields}
                  user={user}
                  isPulseActive={isPulseActive}
                  onSubmitSuccess={() => {
                    fetchEntries();
                    fetchUnverifiedCount();
                    fetchBlockingCommitments();
                  }}
                  unverifiedCount={unverifiedCount}
                  blockingCommitments={blockingCommitments}
                  onShowBlockingModal={() => setShowBlockingModal(true)}
                  suggestions={suggestions}
                  suggestionsLoading={suggestionsLoading}
                  onRefreshSuggestions={fetchSuggestions}
                  onConfirmAuto={handleConfirmAuto}
                />
              </TabsContent>
            </Tabs>
        </CardContent>
        </Card>
      </AttendanceGate>

      {/* Today's Entries Table */}
      <Card>
        <CardHeader className="pb-2 bg-[#1F4E79] text-white rounded-t-lg">
          <CardTitle className="text-base">Entries for {selectedDate} ({entries.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {entries.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No entries for {selectedDate}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="p-2 text-left font-medium">Gym</th>
                    <th className="p-2 text-left font-medium">Pulse</th>
                    <th className="p-2 text-left font-medium">Source</th>
                    <th className="p-2 text-center font-medium">Timestamp</th>
                    <th className="p-2 text-center font-medium">Filled By</th>
                    <th className="p-2 text-center font-medium">Dialed</th>
                    <th className="p-2 text-center font-medium">Call Source</th>
                    <th className="p-2 text-center font-medium">Connected</th>
                    <th className="p-2 text-center font-medium">Connect %</th>
                    <th className="p-2 text-center font-medium">Closures</th>
                    <th className="p-2 text-center font-medium">TL</th>
                    <th className="p-2 text-center font-medium">Mgr</th>
                    <th className="p-2 text-center font-medium">Flag</th>
                    <th className="p-2 text-center font-medium">Edits</th>
                    <th className="p-2 text-center font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`entry-row-${idx}`}>
                      <td className="p-2 font-medium">{entry.gym_name}</td>
                      <td className="p-2">{entry.pulse_name}</td>
                      <td className="p-2">{entry.source_name}</td>
                      <td className="p-2 text-center text-xs text-gray-600">{formatTimestampIST(entry.created_at)}</td>
                      <td className="p-2 text-center text-xs">
                        <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full whitespace-nowrap">
                          {formatFilledBy(entry)}
                        </span>
                      </td>
                      <td className="p-2 text-center">{entry.dialed}</td>
                      <td className="p-2 text-center text-xs">
                        <div className="flex flex-col gap-0.5">
                          <div><span className="text-green-600">IVR: {entry.dialed_company_ivr || 0}</span></div>
                          <div><span className="text-amber-600">FD: {entry.dialed_fd_phone || 0}</span></div>
                        </div>
                      </td>
                      <td className="p-2 text-center">{entry.connected}</td>
                      <td className="p-2 text-center font-medium" style={{ color: getConnectColor(entry.connect_pct) }}>
                        {entry.connect_pct}%
                      </td>
                      <td className="p-2 text-center font-bold text-green-600">{entry.total_closure}</td>
                      <td className="p-2 text-center">
                        {entry.tl_verified ? (
                          <span className="text-green-600">✓</span>
                        ) : (
                          <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => handleVerifyTL(entry.id)}>
                            Verify
                          </Button>
                        )}
                      </td>
                      <td className="p-2 text-center">
                        {entry.mgr_verified ? (
                          <span className="text-green-600">✓</span>
                        ) : user.role !== 'team_leader' ? (
                          <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => handleVerifyManager(entry.id)}>
                            Verify
                          </Button>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="p-2 text-center text-lg">{entry.flag}</td>
                      <td className="p-2 text-center">
                        {(entry.edit_count || 0) > 0 ? (
                          <Badge
                            variant="outline"
                            className="cursor-pointer text-amber-700 border-amber-400 bg-amber-50 hover:bg-amber-100"
                            onClick={() => openEditHistory(entry)}
                          >
                            Edited {entry.edit_count}/3
                          </Badge>
                        ) : (
                          <span className="text-gray-300 text-xs">-</span>
                        )}
                      </td>
                      <td className="p-2 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {/* Edit button: visible for unverified entries with < 3 edits (admin always sees it) */}
                          {(user.role === 'admin' || (!entry.tl_verified && !entry.mgr_verified && (entry.edit_count || 0) < 3)) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs"
                              onClick={() => openEditDialog(entry)}
                            >
                              <Edit size={12} className="mr-1" />
                              Edit
                            </Button>
                          )}
                          {user.role === 'admin' && (
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleDelete(entry.id)}>
                            <Trash2 size={14} className="text-red-500" />
                          </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Mandatory Verification Confirmation Dialog */}
      <AlertDialog open={showVerificationConfirm} onOpenChange={setShowVerificationConfirm}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-amber-100 rounded-full">
                <ShieldCheck className="h-6 w-6 text-amber-600" />
              </div>
              <AlertDialogTitle className="text-lg">Confirm Data Verification</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-left space-y-3">
              <p className="text-gray-700 font-medium">
                Are you sure you have verified this data?
              </p>
              <div className="bg-gray-50 p-3 rounded-lg text-sm space-y-1">
                <p><span className="text-gray-500">Gym:</span> <span className="font-medium">{autoFields.gym_name}</span></p>
                <p><span className="text-gray-500">Pulse:</span> <span className="font-medium">{formData.pulse_id}</span></p>
                <p><span className="text-gray-500">Dialed:</span> <span className="font-medium">{formData.dialed || 0}</span> → <span className="text-gray-500">Connected:</span> <span className="font-medium">{formData.connected || 0}</span></p>
                <p><span className="text-gray-500">Total Closure:</span> <span className="font-medium text-green-600">{totalClosure}</span></p>
              </div>
              <p className="text-amber-700 text-sm bg-amber-50 p-2 rounded border border-amber-200">
                ⚠️ By clicking "Yes, Submit Entry", you confirm that all data entered is accurate and has been verified.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="flex-1">Go Back & Review</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmedSubmit}
              className="flex-1 bg-[#1F4E79] hover:bg-[#163a5c]"
            >
              Yes, Submit Entry
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* FD Availability = 0 Confirmation Dialog
          Flow:
          1. User fills form with FD availability = 0
          2. User clicks Submit
          3. FD=0 confirmation dialog appears (amber warning style)
          4. If user clicks "Go Back & Edit" -> dialog closes, form still filled
          5. If user clicks "Yes, FD is Zero" -> FD dialog closes, TL verification dialog opens
          6. TL verification flow continues as before
      */}
      <AlertDialog open={showFDZeroConfirm} onOpenChange={setShowFDZeroConfirm}>
        <AlertDialogContent className="max-w-md" data-testid="fd-zero-confirm-dialog">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-amber-100 rounded-full">
                <AlertTriangle className="h-6 w-6 text-amber-600" />
              </div>
              <AlertDialogTitle className="text-lg">Zero Front Desk Availability</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-left space-y-3">
              <p className="text-gray-700">
                Are you sure you have zero front desk available today? This is unusual and may indicate a data entry error.
              </p>
              <div className="bg-amber-50 p-3 rounded-lg border border-amber-200 text-sm">
                <p className="text-amber-800 font-medium">
                  FD Availability is set to 0 for {autoFields.gym_name || 'this gym'}.
                </p>
                <p className="text-amber-700 mt-1">
                  If this is correct, confirm below to proceed with submission.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="flex-1" data-testid="fd-zero-cancel-btn">
              Go Back & Edit
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowFDZeroConfirm(false);
                setShowVerificationConfirm(true);
              }}
              className="flex-1 bg-amber-600 hover:bg-amber-700"
              data-testid="fd-zero-confirm-btn"
            >
              Yes, FD is Zero
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Blocking Commitments Modal */}
      <BlockingCommitmentsModal
        open={showBlockingModal}
        onClose={() => setShowBlockingModal(false)}
        commitments={blockingCommitments}
        onResolved={fetchBlockingCommitments}
        userRole={user.role}
      />

      {/* Edit Pulse Entry Dialog */}
      <EditPulseEntryDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        entry={selectedEntryForEdit}
        onSuccess={handleEditSuccess}
      />

      {/* Edit History Dialog */}
      <EditHistoryDialog
        open={editHistoryDialogOpen}
        onOpenChange={setEditHistoryDialogOpen}
        editHistory={selectedEntryForEdit?.edit_history || []}
      />
    </div>
  );
}
